import {
  EMAIL_MAX_LENGTH,
  EMAIL_TOO_LONG_MESSAGE,
  PHONE_MAX_LENGTH,
  PHONE_TOO_LONG_MESSAGE,
  isWithinLength,
} from '@/utils/freeTextValidation'
import { isPossiblePhoneNumber } from 'libphonenumber-js'
import validator from 'validator'

/** Matches ordering flows: non-empty + `validator.isEmail` + within the DB length cap. */
export function isValidOrderContactEmail(value: string | undefined | null): boolean {
  const t = value?.trim()
  if (!t) return false
  if (!isWithinLength(t, EMAIL_MAX_LENGTH)) return false
  return validator.isEmail(t)
}

/**
 * Accepts US national numbers (backward compatible) and international numbers in
 * E.164 / formatted form (e.g. `+44 20 7123 4567`, `+52 55 1234 5678`, `+86 139 1234 5678`).
 *
 * A leading `+` is checked against the country of its dialing code, so any country
 * (Mexican, Chinese, etc.) is accepted when the caller includes the international prefix.
 * A number without a `+` is interpreted as US only — international numbers MUST include
 * their `+` country code to validate. Uses libphonenumber-js `isPossiblePhoneNumber` so
 * validation only checks the number's form/length (not specific area codes or number
 * ranges), and formatting (spaces, dashes, parens) is tolerated. Also enforces the DB
 * length cap so hydrated/copied values that exceed the limit are rejected even though a
 * bare `maxLength` input can't catch them.
 */
export function isValidOrderContactPhone(value: string | undefined | null): boolean {
  const t = value?.trim()
  if (!t) return false
  if (!isWithinLength(t, PHONE_MAX_LENGTH)) return false
  try {
    // Bare national numbers default to US; `+`-prefixed numbers carry their own country code.
    return t.startsWith('+') ? isPossiblePhoneNumber(t) : isPossiblePhoneNumber(t, 'US')
  } catch {
    return false
  }
}

export function orderContactEmailError(value: string | undefined | null): string | undefined {
  const t = value?.trim()
  if (!t) return 'Enter an email address'
  if (!isWithinLength(t, EMAIL_MAX_LENGTH)) return EMAIL_TOO_LONG_MESSAGE
  if (!validator.isEmail(t)) return 'Enter a valid email address'
  return undefined
}

export function orderContactPhoneError(value: string | undefined | null): string | undefined {
  const t = value?.trim()
  if (!t) return 'Enter a phone number'
  if (!isWithinLength(t, PHONE_MAX_LENGTH)) return PHONE_TOO_LONG_MESSAGE
  if (!isValidOrderContactPhone(t))
    return 'Enter a valid phone number. For international numbers, include a country code with +.'
  return undefined
}

/** US-style phone/fax: 10 digits, or 11 with leading country code 1. */
export function isValidUsPhoneDigits(value: string | undefined | null): boolean {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length === 10) return true
  if (digits.length === 11 && digits[0] === '1') return true
  return false
}
